import { useEffect, useRef, useState } from "react";
import { renderInventoryOutlookPdfToBlob } from "@/components/manufacturing/pdf/generateInventoryOutlookPdf";
import { emailCurrentInventoryOutlook, type InventoryOutlookReport } from "@/lib/manufacturing/inventoryMatchClient";

const buttonClass =
  "rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100";

export function InventoryOutlookReportView({ report }: { report: InventoryOutlookReport }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const generatedOn = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  useEffect(() => {
    const held = { url: "" };
    let cancel = false;
    setError(null);
    setNote(null);
    void renderInventoryOutlookPdfToBlob(report, generatedOn).then((out) => {
      if (cancel) return;
      if (!out.ok) {
        setError(out.error);
        return;
      }
      held.url = URL.createObjectURL(out.blob);
      setBlob(out.blob);
      setUrl(held.url);
    });
    return () => {
      cancel = true;
      if (held.url) URL.revokeObjectURL(held.url);
    };
  }, [report, generatedOn]);

  function download() {
    if (!blob) return;
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = `inventory-outlook-${new Date().toISOString().slice(0, 10)}.pdf`;
    link.click();
    URL.revokeObjectURL(href);
  }

  function printReport() {
    const frame = frameRef.current?.contentWindow;
    if (!frame) return;
    frame.focus();
    frame.print();
  }

  async function emailMe() {
    setBusy(true);
    setError(null);
    setNote(null);
    const out = await emailCurrentInventoryOutlook(report);
    setBusy(false);
    if (!out.ok) {
      setError(out.error);
      return;
    }
    setNote(`Sent to ${out.data.sent_to}`);
  }

  return (
    <section className="rounded-2xl bg-zinc-200 p-4 dark:bg-zinc-800">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button type="button" className={buttonClass} disabled={!blob} onClick={download}>
          Download
        </button>
        <button type="button" className={buttonClass} disabled={busy} onClick={() => void emailMe()}>
          Email to me
        </button>
        <button type="button" className={buttonClass} disabled={!url} onClick={printReport}>
          Print
        </button>
        {note ? <span className="text-sm text-gray-700 dark:text-zinc-200">{note}</span> : null}
      </div>
      {error ? <p className="mb-3 text-sm text-red-800 dark:text-red-200">{error}</p> : null}
      {url ? (
        <iframe
          ref={frameRef}
          title="Inventory outlook report"
          src={url}
          className="h-[80vh] w-full rounded-sm bg-white shadow-lg"
        />
      ) : (
        <p className="text-sm text-gray-700 dark:text-zinc-200">Building the report…</p>
      )}
    </section>
  );
}
